import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../components/base/Modal";

// Formulario modal declarativo para las altas/ediciones de la consola.

export interface FormField {
  key: string;
  label: string;
  type?: "text" | "number" | "select" | "textarea" | "datetime" | "checkbox" | "multiselect";
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  full?: boolean;
  hint?: string;
}

export type FormValues = Record<string, string | number | boolean | string[]>;

const inputCls = "w-full rounded-lg border border-line bg-app px-3 py-2 text-sm text-content outline-none focus:border-primary-500";

export default function FormModal({
  title,
  fields,
  initial = {},
  submitLabel,
  busy = false,
  onSubmit,
  onClose,
  extra,
}: {
  title: string;
  fields: FormField[];
  initial?: FormValues;
  submitLabel: string;
  busy?: boolean;
  onSubmit: (values: FormValues) => void | Promise<void>;
  onClose: () => void;
  extra?: ReactNode;
}) {
  const { t } = useTranslation("accessConsole");
  const [values, setValues] = useState<FormValues>(() => {
    const v: FormValues = {};
    for (const f of fields) v[f.key] = initial[f.key] ?? (f.type === "checkbox" ? false : f.type === "multiselect" ? [] : "");
    return v;
  });
  const set = (k: string, v: FormValues[string]) => setValues((p) => ({ ...p, [k]: v }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void onSubmit(values);
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      width="max-w-2xl w-full"
      disableCRUD
      content={
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {fields.map((f) => (
              <label key={f.key} className={`block text-sm ${f.full || f.type === "textarea" || f.type === "multiselect" ? "sm:col-span-2" : ""}`}>
                {f.type !== "checkbox" && (
                  <span className="mb-1 block text-content-secondary">
                    {f.label}
                    {f.required && <span className="text-error"> *</span>}
                  </span>
                )}
                {f.type === "select" ? (
                  <select className={inputCls} value={String(values[f.key] ?? "")} required={f.required} onChange={(e) => set(f.key, e.target.value)}>
                    {!f.required && <option value="">—</option>}
                    {(f.options ?? []).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : f.type === "multiselect" ? (
                  <div className="flex max-h-36 flex-wrap gap-1.5 overflow-y-auto rounded-lg border border-line bg-app p-2">
                    {(f.options ?? []).map((o) => {
                      const arr = (values[f.key] as string[]) ?? [];
                      const on = arr.includes(o.value);
                      return (
                        <button
                          key={o.value}
                          type="button"
                          aria-pressed={on}
                          onClick={() => set(f.key, on ? arr.filter((x) => x !== o.value) : [...arr, o.value])}
                          className={`rounded-full border px-2.5 py-1 text-xs ${on ? "border-primary-500 bg-primary-500/15 text-primary-500" : "border-line text-content-secondary hover:bg-subtle"}`}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                ) : f.type === "textarea" ? (
                  <textarea className={inputCls} rows={3} value={String(values[f.key] ?? "")} placeholder={f.placeholder} required={f.required} onChange={(e) => set(f.key, e.target.value)} />
                ) : f.type === "checkbox" ? (
                  <span className="flex items-center gap-2 pt-6 text-content">
                    <input type="checkbox" checked={Boolean(values[f.key])} onChange={(e) => set(f.key, e.target.checked)} className="h-4 w-4 accent-primary-500" />
                    {f.label}
                  </span>
                ) : (
                  <input
                    className={inputCls}
                    type={f.type === "number" ? "number" : f.type === "datetime" ? "datetime-local" : "text"}
                    value={String(values[f.key] ?? "")}
                    placeholder={f.placeholder}
                    required={f.required}
                    min={f.min}
                    max={f.max}
                    onChange={(e) => set(f.key, f.type === "number" ? e.target.value : e.target.value)}
                  />
                )}
                {f.hint && <span className="mt-1 block text-[11px] text-content-muted">{f.hint}</span>}
              </label>
            ))}
          </div>
          {extra}
          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <button type="button" onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm text-content hover:bg-subtle">{t("common.cancel")}</button>
            <button type="submit" disabled={busy} className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-400 disabled:opacity-60">
              {busy ? t("common.saving") : submitLabel}
            </button>
          </div>
        </form>
      }
    />
  );
}

/** `Date` → valor de un `<input type="datetime-local">` en hora local. */
export function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
